import React, { useState, useEffect, useCallback } from 'react';
import { CheckCircle2, PlusCircle, Trash2, Send, CreditCard, User, Landmark, MapPin, Lock } from 'lucide-react';
import { colors, API, fmt, authHeaders } from '../utils/theme';
import { PageHeader, Badge, Button, LoadingSpinner } from '../components/UI';
import useIsMobile from '../hooks/useIsMobile';

// Meu cadastro (14/09/2026): o investidor preenche os próprios dados, PIX e
// cartões e envia pra aprovação da gestão. Cartão fica "aguardando aprovação"
// até lá. O termo aceito é o da modalidade dele (texto colado pela gestão).
async function api(path, opts = {}) {
  const r = await fetch(`${API}/meu-cadastro${path}`, {
    ...opts, headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(opts.headers || {}) },
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
  return d;
}
const inp = {
  width: '100%', padding: '9px 10px', borderRadius: 8, border: `1px solid ${colors.border}`,
  background: colors.bg, color: colors.text, fontSize: 14, boxSizing: 'border-box',
};
const lbl = { fontSize: 12, color: colors.textMuted, display: 'block', marginBottom: 4 };
const card = { background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 12, padding: 18, marginBottom: 16 };
const UFS = ['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'];
const BANDEIRAS = ['VISA', 'MASTERCARD', 'ELO', 'AMEX', 'HIPERCARD', 'OUTRA'];
const PIX_TIPOS = ['CPF', 'E-mail', 'Telefone', 'Chave aleatória'];
const STATUS_TXT = {
  CONVIDADO: { txt: 'Complete seu cadastro e envie pra aprovação', cor: colors.warning },
  ENVIADO: { txt: 'Cadastro enviado — em análise pela gestão', cor: colors.info },
  APROVADO: { txt: 'Cadastro aprovado', cor: colors.success },
};

// Celular: a grade vira duas colunas, e campo largo (span ≥ 2) ocupa a linha.
function Campo({ label, children, span }) {
  const mobile = useIsMobile();
  const coluna = !span ? undefined : mobile ? (span >= 2 ? '1 / -1' : undefined) : `span ${span}`;
  return <div style={{ gridColumn: coluna, minWidth: mobile ? 0 : undefined }}><label style={lbl}>{label}</label>{children}</div>;
}

export default function MeuCadastroPage() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [bancos, setBancos] = useState([]);
  const [erro, setErro] = useState('');
  const [msg, setMsg] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [aceite, setAceite] = useState(false);
  // `mesmo_cpf` e `mesmo_endereco` começam marcados: o caso comum é o cartão
  // estar no nome e no endereço do próprio investidor. Quem tem cartão de
  // terceiro desmarca. O servidor é quem copia os dados — ver lib/cofre-cartao.
  const CARTAO_VAZIO = {
    banco_id: '', bandeira: 'VISA', numeracao_final: '', limite_total: '', dia_vencimento: '', dia_fechamento: '', nome_cartao: '',
    numero: '', validade: '', cvv: '', nome_impresso: '',
    mesmo_cpf: true, cpf_titular_cartao: '',
    mesmo_endereco: true, cobranca_cep: '', cobranca_logradouro: '', cobranca_numero: '',
    cobranca_complemento: '', cobranca_bairro: '', cobranca_cidade: '', cobranca_uf: '',
  };
  const [novo, setNovo] = useState(CARTAO_VAZIO);
  const mobile = useIsMobile();

  const carregar = useCallback(async () => {
    try {
      const d = await api('');
      setData(d);
      const t = d.titular || {};
      setForm(Object.fromEntries(Object.entries(t).map(([k, v]) => [k, v == null ? '' : v])));
      setErro('');
    } catch (e) { setErro(e.message); }
  }, []);
  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => {
    fetch(`${API}/bancos`, { headers: authHeaders() }).then(r => r.json())
      .then(d => setBancos((Array.isArray(d) ? d : (d?.data || [])).slice().sort((a, b) => String(a.nome).localeCompare(String(b.nome)))))
      .catch(() => setBancos([]));
  }, []);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const salvar = async () => {
    setSalvando(true); setErro(''); setMsg('');
    try {
      const body = {};
      ['nome', 'cpf', 'data_nascimento', 'telefone', 'email', 'cep', 'endereco_logradouro', 'endereco_numero', 'endereco_complemento',
        'endereco_bairro', 'endereco_cidade', 'endereco_uf', 'pix_tipo', 'pix_chave', 'banco_recebimento', 'agencia_recebimento', 'conta_recebimento']
        .forEach(k => { body[k] = form[k] ?? ''; });
      setData(await api('', { method: 'PUT', body: JSON.stringify(body) }));
      setMsg('Dados salvos.');
    } catch (e) { setErro(e.message); }
    setSalvando(false);
  };
  const addCartao = async () => {
    setSalvando(true); setErro('');
    try {
      const r = await api('/cartoes', { method: 'POST', body: JSON.stringify(novo) });
      setNovo(CARTAO_VAZIO);
      setMsg(r?.no_cofre ? 'Cartão adicionado e dados guardados no cofre.' : 'Cartão adicionado.');
      await carregar();
    } catch (e) { setErro(e.message); }
    setSalvando(false);
  };
  const removerCartao = async (c) => {
    if (!window.confirm(`Remover o cartão ${c.banco || ''} final ${c.numeracao_final}?`)) return;
    try { await api(`/cartoes/${c.id}`, { method: 'DELETE' }); await carregar(); } catch (e) { setErro(e.message); }
  };
  const enviar = async () => {
    if (!aceite) { setErro('Marque o aceite do termo pra enviar'); return; }
    setSalvando(true); setErro('');
    try { setData(await api('/enviar', { method: 'POST', body: JSON.stringify({ aceite: true }) })); setMsg('Cadastro enviado! A gestão vai revisar e aprovar.'); }
    catch (e) { setErro(e.message); }
    setSalvando(false);
  };

  if (erro && !data) return <div style={{ color: colors.danger, padding: 20 }}>{erro}</div>;
  if (!data) return <LoadingSpinner />;
  const st = STATUS_TXT[data.titular.cadastro_status] || STATUS_TXT.CONVIDADO;
  // Celular: duas colunas (Campo põe o campo largo na linha inteira).
  const grid = { display: 'grid', gridTemplateColumns: mobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4, 1fr)', gap: 12 };
  const grid6 = { ...grid, gridTemplateColumns: mobile ? grid.gridTemplateColumns : 'repeat(6, 1fr)', alignItems: 'end' };
  const linhaToda = (desktop) => (mobile ? '1 / -1' : desktop);

  return (
    <div style={{ maxWidth: 1000 }}>
      <PageHeader title="Meu cadastro" subtitle="Dados, PIX de recebimento e cartões cedidos à operação" />
      <div style={{ ...card, borderColor: `${st.cor}66`, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <Badge text={data.titular.cadastro_status || 'CONVIDADO'} color={st.cor} />
        <span style={{ color: colors.text }}>{st.txt}</span>
        <span style={{ flex: 1 }} />
        {data.taxa_pct != null && <span style={{ fontSize: 12, color: colors.textMuted }}>Remuneração: <b style={{ color: colors.text }}>{String(data.taxa_pct).replace('.', ',')}% a.m.</b>{data.pontos_organicos_investidor ? ' · pontos orgânicos ficam com você' : ' · pontos orgânicos ficam com a operação'}</span>}
        {data.faltam.length > 0 && <span style={{ fontSize: 12, color: colors.warning }}>Faltam: {data.faltam.join(', ')}</span>}
      </div>
      {erro && <div style={{ color: colors.danger, marginBottom: 12 }}>{erro}</div>}
      {msg && <div style={{ color: colors.success, marginBottom: 12 }}>{msg}</div>}

      <div style={card}>
        <div style={{ fontWeight: 700, color: colors.text, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}><User size={16} /> Dados pessoais</div>
        <div style={grid}>
          <Campo label="Nome completo *" span={2}><input style={inp} value={form.nome || ''} onChange={set('nome')} /></Campo>
          <Campo label="CPF *"><input style={inp} value={form.cpf || ''} onChange={set('cpf')} placeholder="somente números" /></Campo>
          <Campo label="Data de nascimento"><input style={inp} type="date" value={form.data_nascimento || ''} onChange={set('data_nascimento')} /></Campo>
          <Campo label="WhatsApp *"><input style={inp} value={form.telefone || ''} onChange={set('telefone')} placeholder="(11) 9 9999-9999" /></Campo>
          <Campo label="E-mail *" span={2}><input style={inp} type="email" value={form.email || ''} onChange={set('email')} /></Campo>
        </div>
      </div>

      <div style={card}>
        <div style={{ fontWeight: 700, color: colors.text, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}><MapPin size={16} /> Endereço</div>
        <div style={grid}>
          <Campo label="CEP"><input style={inp} value={form.cep || ''} onChange={set('cep')} /></Campo>
          <Campo label="Logradouro" span={2}><input style={inp} value={form.endereco_logradouro || ''} onChange={set('endereco_logradouro')} /></Campo>
          <Campo label="Número"><input style={inp} value={form.endereco_numero || ''} onChange={set('endereco_numero')} /></Campo>
          <Campo label="Complemento"><input style={inp} value={form.endereco_complemento || ''} onChange={set('endereco_complemento')} /></Campo>
          <Campo label="Bairro"><input style={inp} value={form.endereco_bairro || ''} onChange={set('endereco_bairro')} /></Campo>
          <Campo label="Cidade"><input style={inp} value={form.endereco_cidade || ''} onChange={set('endereco_cidade')} /></Campo>
          <Campo label="UF"><select style={inp} value={form.endereco_uf || ''} onChange={set('endereco_uf')}><option value="">—</option>{UFS.map(u => <option key={u}>{u}</option>)}</select></Campo>
        </div>
      </div>

      <div style={card}>
        <div style={{ fontWeight: 700, color: colors.text, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}><Landmark size={16} /> Recebimento da remuneração</div>
        <div style={grid}>
          <Campo label="Tipo da chave PIX"><select style={inp} value={form.pix_tipo || ''} onChange={set('pix_tipo')}><option value="">—</option>{PIX_TIPOS.map(p => <option key={p}>{p}</option>)}</select></Campo>
          <Campo label="Chave PIX *" span={3}><input style={inp} value={form.pix_chave || ''} onChange={set('pix_chave')} /></Campo>
          <Campo label="Banco" span={2}><input style={inp} value={form.banco_recebimento || ''} onChange={set('banco_recebimento')} /></Campo>
          <Campo label="Agência"><input style={inp} value={form.agencia_recebimento || ''} onChange={set('agencia_recebimento')} /></Campo>
          <Campo label="Conta"><input style={inp} value={form.conta_recebimento || ''} onChange={set('conta_recebimento')} /></Campo>
        </div>
        <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={salvar} disabled={salvando} icon={CheckCircle2} color={colors.primary}>Salvar dados</Button>
        </div>
      </div>

      <div style={card}>
        <div style={{ fontWeight: 700, color: colors.text, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}><CreditCard size={16} /> Cartões cedidos à operação</div>
        {data.cartoes.length === 0 ? (
          <div style={{ color: colors.textDim, fontSize: 13, marginBottom: 12 }}>Nenhum cartão ainda — cadastre abaixo os cartões que você cede.</div>
        ) : (
          <div style={{ overflowX: 'auto', marginBottom: 14 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ borderBottom: `1px solid ${colors.border}` }}>
              {['Banco', 'Bandeira', 'Final', 'Limite', 'Venc.', 'Fech.', 'Cofre', 'Situação', ''].map(h => <th key={h} style={{ padding: '6px 8px', textAlign: 'left', fontSize: 11, color: colors.textMuted, textTransform: 'uppercase' }}>{h}</th>)}
            </tr></thead>
            <tbody>{data.cartoes.map(c => (
              <tr key={c.id} style={{ borderBottom: `1px solid ${colors.border}22` }}>
                <td style={{ padding: '6px 8px', color: colors.text }}>{c.banco || '—'}<div style={{ fontSize: 11, color: colors.textDim }}>{c.nome_cartao}</div></td>
                <td style={{ padding: '6px 8px', color: colors.text }}>{c.bandeira || '—'}</td>
                <td style={{ padding: '6px 8px', color: colors.text }}>{c.numeracao_final || '—'}</td>
                <td style={{ padding: '6px 8px', color: colors.text }}>{fmt(c.limite_total)}</td>
                <td style={{ padding: '6px 8px', color: colors.text }}>{c.data_vencimento ? `dia ${String(c.data_vencimento).slice(-2)}` : '—'}</td>
                <td style={{ padding: '6px 8px', color: colors.text }}>{c.dia_fechamento ? `dia ${c.dia_fechamento}` : '—'}</td>
                {/* O cartão só serve pra operação comprar se os dados estiverem
                    no cofre; sem isso, ninguém consegue usar o limite cedido. */}
                <td style={{ padding: '6px 8px' }}>
                  {c.tem_cofre
                    ? <Badge text="GUARDADO" color={colors.success} />
                    : <span style={{ fontSize: 11, color: colors.warning }}>faltam os dados</span>}
                </td>
                <td style={{ padding: '6px 8px' }}><Badge text={c.status === 'AGUARDANDO_APROVACAO' ? 'AGUARDANDO APROVAÇÃO' : c.status} color={c.status === 'AGUARDANDO_APROVACAO' ? colors.warning : colors.success} /></td>
                <td style={{ padding: '6px 8px' }}>{c.status === 'AGUARDANDO_APROVACAO' && (
                  <button onClick={() => removerCartao(c)} title="Remover" style={{ background: 'none', border: 'none', color: colors.danger, cursor: 'pointer' }}><Trash2 size={14} /></button>
                )}</td>
              </tr>
            ))}</tbody>
          </table>
          </div>
        )}
        <div style={grid6}>
          <Campo label="Banco *" span={2}><select style={inp} value={novo.banco_id} onChange={e => setNovo(n => ({ ...n, banco_id: e.target.value }))}><option value="">Selecione…</option>{bancos.map(b => <option key={b.id} value={b.id}>{b.nome}</option>)}</select></Campo>
          <Campo label="Bandeira"><select style={inp} value={novo.bandeira} onChange={e => setNovo(n => ({ ...n, bandeira: e.target.value }))}>{BANDEIRAS.map(b => <option key={b}>{b}</option>)}</select></Campo>
          {/* Com o número completo preenchido abaixo, os 4 últimos saem dele
              sozinhos — um campo a menos e zero chance de divergir. */}
          <Campo label={novo.numero ? '4 últimos (do número)' : '4 últimos dígitos *'}>
            <input style={{ ...inp, opacity: novo.numero ? 0.6 : 1 }} maxLength={4} disabled={!!novo.numero}
              value={novo.numero ? novo.numero.replace(/\D/g, '').slice(-4) : novo.numeracao_final}
              onChange={e => setNovo(n => ({ ...n, numeracao_final: e.target.value.replace(/\D/g, '') }))} />
          </Campo>
          <Campo label="Limite total (R$) *"><input style={inp} value={novo.limite_total} onChange={e => setNovo(n => ({ ...n, limite_total: e.target.value }))} placeholder="ex: 25000" /></Campo>
          <Campo label="Dia venc. *"><input style={inp} type="number" min="1" max="31" value={novo.dia_vencimento} onChange={e => setNovo(n => ({ ...n, dia_vencimento: e.target.value }))} /></Campo>
          <Campo label="Dia fech."><input style={inp} type="number" min="1" max="31" value={novo.dia_fechamento} onChange={e => setNovo(n => ({ ...n, dia_fechamento: e.target.value }))} /></Campo>
          {/* Convenção da casa: TITULAR + BANCO + MODELO. É por esse nome que o
              cartão é achado em conciliação, fatura e cofre, então nome solto
              (só "Bradesco") deixa dois cartões indistinguíveis. */}
          <Campo label="Nome do cartão (opcional)" span={3}>
            <input style={inp} value={novo.nome_cartao} onChange={e => setNovo(n => ({ ...n, nome_cartao: e.target.value }))} placeholder="MURILO BRADESCO AMEX PLATINUM" />
            <div style={{ fontSize: 11, color: colors.textDim, marginTop: 3 }}>
              Use <b>titular + banco + modelo</b>, como “MURILO BRADESCO AMEX PLATINUM”. Se deixar em branco, o sistema monta um nome e a gestão pode ajustar depois.
            </div>
          </Campo>
        </div>

        {/* ── Dados de compra (cofre) ─────────────────────────────────────── */}
        <div style={{ marginTop: 16, padding: 12, borderRadius: 10, border: `1px solid ${colors.border}`, background: `${colors.bgLight}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, flexWrap: 'wrap' }}>
            <Lock size={14} color={colors.warning} />
            <span style={{ fontWeight: 700, color: colors.text, fontSize: 13 }}>Dados para compra (cofre)</span>
            <span style={{ fontSize: 11, color: colors.textDim }}>opcional agora, necessário para a operação usar o cartão</span>
          </div>
          <div style={{ fontSize: 11, color: colors.textDim, marginBottom: 10, lineHeight: 1.5 }}>
            Número, validade e CVV são guardados criptografados e só aparecem para você, para a gestão e para quem tem acesso ao cofre. Toda leitura fica registrada.
          </div>
          <div style={grid6}>
            <Campo label="Número do cartão" span={2}>
              <input style={inp} inputMode="numeric" autoComplete="off" placeholder="0000 0000 0000 0000"
                value={novo.numero} onChange={e => setNovo(n => ({ ...n, numero: e.target.value.replace(/[^\d ]/g, '').slice(0, 24) }))} />
            </Campo>
            <Campo label="Validade (MM/AA)">
              <input style={inp} placeholder="09/29" maxLength={7} autoComplete="off"
                value={novo.validade} onChange={e => setNovo(n => ({ ...n, validade: e.target.value }))} />
            </Campo>
            <Campo label="CVV">
              <input style={inp} inputMode="numeric" maxLength={4} autoComplete="off"
                value={novo.cvv} onChange={e => setNovo(n => ({ ...n, cvv: e.target.value.replace(/\D/g, '') }))} />
            </Campo>
            <Campo label="Nome impresso no cartão" span={2}>
              <input style={inp} autoComplete="off" placeholder="como está gravado no plástico"
                value={novo.nome_impresso} onChange={e => setNovo(n => ({ ...n, nome_impresso: e.target.value }))} />
            </Campo>

            <Campo label="CPF do titular do cartão" span={2}>
              <input style={{ ...inp, opacity: novo.mesmo_cpf ? 0.6 : 1 }} disabled={novo.mesmo_cpf} autoComplete="off"
                placeholder={novo.mesmo_cpf ? 'usando o CPF do seu cadastro' : 'somente números'}
                value={novo.mesmo_cpf ? '' : novo.cpf_titular_cartao}
                onChange={e => setNovo(n => ({ ...n, cpf_titular_cartao: e.target.value.replace(/\D/g, '').slice(0, 11) }))} />
            </Campo>
            <div style={{ gridColumn: linhaToda('span 4'), paddingBottom: 8 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: colors.text, cursor: 'pointer' }}>
                <input type="checkbox" checked={novo.mesmo_cpf} onChange={e => setNovo(n => ({ ...n, mesmo_cpf: e.target.checked }))} />
                O cartão está no meu CPF (repetir o do meu cadastro)
              </label>
            </div>

            <div style={{ gridColumn: linhaToda('span 6'), paddingBottom: 4 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: colors.text, cursor: 'pointer' }}>
                <input type="checkbox" checked={novo.mesmo_endereco} onChange={e => setNovo(n => ({ ...n, mesmo_endereco: e.target.checked }))} />
                Endereço de cobrança é o mesmo do meu cadastro
              </label>
            </div>
            {!novo.mesmo_endereco && (<>
              <Campo label="CEP de cobrança"><input style={inp} value={novo.cobranca_cep} onChange={e => setNovo(n => ({ ...n, cobranca_cep: e.target.value }))} /></Campo>
              <Campo label="Logradouro" span={3}><input style={inp} value={novo.cobranca_logradouro} onChange={e => setNovo(n => ({ ...n, cobranca_logradouro: e.target.value }))} /></Campo>
              <Campo label="Número"><input style={inp} value={novo.cobranca_numero} onChange={e => setNovo(n => ({ ...n, cobranca_numero: e.target.value }))} /></Campo>
              <Campo label="Complemento"><input style={inp} value={novo.cobranca_complemento} onChange={e => setNovo(n => ({ ...n, cobranca_complemento: e.target.value }))} /></Campo>
              <Campo label="Bairro" span={2}><input style={inp} value={novo.cobranca_bairro} onChange={e => setNovo(n => ({ ...n, cobranca_bairro: e.target.value }))} /></Campo>
              <Campo label="Cidade" span={2}><input style={inp} value={novo.cobranca_cidade} onChange={e => setNovo(n => ({ ...n, cobranca_cidade: e.target.value }))} /></Campo>
              <Campo label="UF"><input style={inp} maxLength={2} value={novo.cobranca_uf} onChange={e => setNovo(n => ({ ...n, cobranca_uf: e.target.value.toUpperCase() }))} /></Campo>
            </>)}
          </div>
        </div>

        <div style={{ marginTop: 12 }}>
          <Button onClick={addCartao} disabled={salvando} icon={PlusCircle} color={colors.success}>Adicionar cartão</Button>
        </div>
      </div>

      <div style={card}>
        <div style={{ fontWeight: 700, color: colors.text, marginBottom: 8 }}>{data.modalidade === 'SCP' ? 'Contrato de SCP' : 'Contrato de mútuo mercantil com cessão de limite'}</div>
        {/* A assinatura de verdade acontece na D4Sign, depois da aprovação. Sem
            dizer isso, o texto aqui parece o contrato sendo fechado nesta tela
            e o investidor acha que já assinou. */}
        <div style={{ padding: 12, borderRadius: 8, background: `${colors.info}12`, border: `1px solid ${colors.info}44`, color: colors.text, fontSize: 13, lineHeight: 1.6, marginBottom: 10 }}>
          <b>Este é o texto do contrato, para você ler antes.</b> A assinatura não acontece aqui.
          Depois que a gestão aprovar o seu cadastro, o contrato chega no seu e-mail pela <b>D4Sign</b>,
          já preenchido com os seus dados, para assinatura eletrônica com validade jurídica.
          Marcar a caixa abaixo é só a sua confirmação de que leu e de que os dados estão corretos.
        </div>
        <details>
          <summary style={{ cursor: 'pointer', color: colors.info, fontSize: 13, marginBottom: 8 }}>Ler o contrato na íntegra</summary>
          <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: 13, color: colors.text, background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: 8, padding: 14, maxHeight: 320, overflowY: 'auto', margin: '8px 0 0' }}>{data.termo.texto}</pre>
        </details>
        {data.titular.termo_aceite_em && (
          <div style={{ fontSize: 12, color: colors.success, marginTop: 8 }}>Lido e confirmado em {new Date(data.titular.termo_aceite_em).toLocaleString('pt-BR')} (versão {data.titular.termo_aceite_versao})</div>
        )}
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 12, color: colors.text, fontSize: 14, cursor: 'pointer' }}>
          <input type="checkbox" checked={aceite} onChange={e => setAceite(e.target.checked)} style={{ marginTop: 3 }} />
          <span>Li o contrato, meus dados e cartões estão corretos, e estou de acordo em recebê-lo pela D4Sign para assinar.</span>
        </label>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
          <Button onClick={enviar} disabled={salvando || !data.pode_enviar || !aceite} icon={Send} color={colors.primary}
            title={data.pode_enviar ? 'Envia pra aprovação da gestão' : `Faltam: ${data.faltam.join(', ')}`}>
            {data.titular.cadastro_status === 'APROVADO' ? 'Reenviar alterações' : 'Enviar cadastro'}
          </Button>
        </div>
      </div>
    </div>
  );
}
